import { Avatar, AvatarFallback } from "@repo/ui";
import { Badge } from "@repo/ui";
import { Clock, Paperclip } from "lucide-react";
import { COLORS_MAPPING } from "../../lib/constants";
import type { MailReponse, MailMetadata } from "../../lib/types";
import { MailStatus } from "../../lib/types";
import {
  calculateDateWithDateRelative,
  getInitials,
  getSenderColor,
} from "../../lib/utils";
import { cn } from "@repo/utils";
import { useUIStore } from "../../stores/uiStore";
import type { TypeReponseMail } from "../../templates/types";

interface MailRowProps {
  mailMetadata: MailMetadata;
  mailResponse?: MailReponse;
  typeReponse?: TypeReponseMail;
  isPresidentMail?: boolean;
  showPrioritySelection?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
}

export function MailRow({
  mailMetadata,
  mailResponse,
  typeReponse,
  isSelected = false,
  onSelect,
}: MailRowProps) {
  const todayDate = useUIStore((state) => state.todayDate);

  const hasAttachments =
    mailMetadata.piecesJointes && mailMetadata.piecesJointes.length > 0;
  const dateText = calculateDateWithDateRelative(
    mailMetadata.dateRelative,
    todayDate,
  );

  const isUnread = !mailResponse || mailResponse.statut === MailStatus.NON_LU;
  const isAnswered = mailResponse?.statut === MailStatus.REPONDU;
  const isRead = mailResponse?.statut === MailStatus.LU;

  const handleClick = () => {
    onSelect?.();
  };

  // Variant et libellé du badge de statut
  const getStatusBadge = () => {
    if (isAnswered) {
      return { variant: "success" as const, text: "Répondu" };
    }
    if (isRead && typeReponse !== "no_response") {
      return { variant: "warning" as const, text: "En attente" };
    }
    if (isRead) {
      return { variant: "default" as const, text: "Lu" };
    }
    return { variant: "outline" as const, text: "Non lu" };
  };

  const statusBadge = getStatusBadge();

  const senderColor = getSenderColor(mailMetadata.emetteur);

  return (
    // Vraie cible d'interaction : `role="button"` + `tabIndex` + clavier. Une
    // ligne de mail n'était qu'un `div` cliquable — inatteignable au clavier et
    // sans nom accessible, donc invisible aux lecteurs d'écran comme aux tests.
    // Le nom accessible porte le sujet ET l'émetteur, les deux seules choses qui
    // distinguent deux lignes entre elles.
    <div
      role="button"
      tabIndex={0}
      aria-label={`${mailMetadata.titre} — de ${mailMetadata.emetteur}`}
      aria-current={isSelected || undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        "group relative flex cursor-pointer items-start gap-3 p-3 transition-all hover:bg-accent/50",
        // Contour de focus INTÉRIEUR : la ligne occupe toute la largeur de la
        // zone défilante, qui rognait un contour extérieur sur les côtés.
        "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
        isUnread && "bg-accent/30",
        isSelected && "bg-primary/10 border-l-4 border-l-primary",
        !isSelected && "border-l-4 border-l-transparent",
      )}
    >
      {/* Avatar with color indicator */}
      <div className="relative">
        <Avatar className="h-10 w-10" style={{ backgroundColor: senderColor }}>
          <AvatarFallback
            className="text-sm font-semibold text-white"
            style={{ backgroundColor: senderColor }}
          >
            {getInitials(mailMetadata.emetteur)}
          </AvatarFallback>
        </Avatar>
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1 space-y-1">
        {/* Header row: sender + date */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={cn(
                "truncate text-sm",
                isUnread
                  ? "font-semibold text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {mailMetadata.emetteur}
            </span>
            {mailMetadata.emetteurEntreprise && (
              <span className="hidden truncate text-xs text-muted-foreground sm:inline">
                ({mailMetadata.emetteurEntreprise})
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{dateText}</span>
          </div>
        </div>

        {/* Subject line with color indicator */}
        <div className="flex items-center gap-2">
          {mailResponse?.couleur && mailResponse.couleur !== "bg-white" && (
            <span
              className={cn(
                "h-3 w-3 shrink-0 rounded-full",
                mailResponse.couleur,
              )}
              title={
                COLORS_MAPPING[
                  mailResponse.couleur as keyof typeof COLORS_MAPPING
                ]
              }
            />
          )}
          <p
            className={cn(
              "truncate text-sm",
              isUnread ? "font-medium text-foreground" : "text-foreground/80",
            )}
          >
            {mailMetadata.titre}
          </p>
        </div>

        {/* Footer row: badges + indicators */}
        <div className="flex items-center gap-2 pt-1">
          <Badge variant={statusBadge.variant} className="text-xs whitespace-nowrap">
            {statusBadge.text}
          </Badge>

          {hasAttachments && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Paperclip className="h-3 w-3" />
              <span>{mailMetadata.piecesJointes.length}</span>
            </div>
          )}

          {mailMetadata.emetteurPoste && (
            <span className="hidden text-xs text-muted-foreground lg:inline">
              {mailMetadata.emetteurPoste}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
